<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { MarkdownRenderer } from '@legere/markdown-renderer';
  import { getBookStorage } from '../lib/storage';
  import { ReadingPositionTracker } from '../lib/reading-position';
  import { tocVisible, tocToggle, currentBook } from '../stores/reader';
  import type { Book } from '../../../../packages/storage-manager/src/index';
  import type { TOCEntry } from '../../../../packages/markdown-renderer/src/index';

  // Book id can be provided via prop or derived from route params/hash
  export let bookId: string = '';
  export let route: any = null;
  export let onSidebarToggleChange: ((toggle: () => void) => void) | null = null;

  let book: Book | null = null;
  let renderedContent = '';
  let toc: TOCEntry[] = [];
  let loading = true;
  let error: string | null = null;
  let contentContainer: HTMLElement | null = null;
  let actualBookId: string = '';
  let positionTracker: ReadingPositionTracker | null = null;
  let assetUrls: Map<string, string> = new Map();

  // Sidebar open by default on desktop, closed on mobile
  let sidebarOpen = false;
  let sidebarWidth = 256; // 16rem = 256px
  let isResizing = false;

  const renderer = new MarkdownRenderer();

  function startResize(e: MouseEvent) {
    isResizing = true;
    e.preventDefault();
  }

  function stopResize() {
    isResizing = false;
  }

  function handleResize(e: MouseEvent) {
    if (!isResizing) return;
    // Min width 200px, max width 600px
    const newWidth = Math.min(Math.max(e.clientX, 200), 600);
    sidebarWidth = newWidth;
  }

  onMount(() => {
    window.addEventListener('mousemove', handleResize);
    window.addEventListener('mouseup', stopResize);

    return () => {
      window.removeEventListener('mousemove', handleResize);
      window.removeEventListener('mouseup', stopResize);
    };
  });

  function toggleSidebar() {
    sidebarOpen = !sidebarOpen;
    tocVisible.set(sidebarOpen);
  }

  // Expose toggle function via store
  tocToggle.set(toggleSidebar);

  // Add to window for debugging
  if (typeof window !== 'undefined') {
    (window as any).__READER_MOUNTED = true;
    console.log('Reader component created');
  }

  onMount(async () => {
    if (typeof window !== 'undefined') {
      (window as any).__READER_ONMOUNT_CALLED = true;

      // Set initial sidebar state based on screen size
      if (window.innerWidth >= 768) {
        sidebarOpen = true;
        tocVisible.set(true);
      }
    }

    // Provide toggle function to parent
    if (onSidebarToggleChange) {
      onSidebarToggleChange(toggleSidebar);
    }

    // Determine book ID from router params or fallback to URL
    console.log('=== READER DEBUG START ===');
    console.log('Reader onMount - URL:', window.location.href);
    console.log('Hash:', window.location.hash);
    console.log('bookId prop from router:', bookId);

    actualBookId = bookId || route?.result?.path?.params?.bookId || '';

    // Fallback: try to get from URL (hash or pathname)
    if (!actualBookId) {
      const hashPath = window.location.hash.startsWith('#/') ? window.location.hash.slice(1).split('?')[0] : '';
      const pathname = window.location.pathname;
      const source = hashPath || pathname;
      const match = source.match(/\/reader\/([^/?#]+)/);
      if (match) {
        actualBookId = match[1];
        console.log('Extracted bookId from URL path:', actualBookId);
      }
    }

    console.log('Reader - Final Book ID:', actualBookId);

    if (!actualBookId) {
      error = 'No book ID provided';
      loading = false;
      console.error('No book ID in URL or route params');
      console.log('=== READER DEBUG END (NO ID) ===');
      return;
    }

    positionTracker = new ReadingPositionTracker(actualBookId);

    try {
      const storage = getBookStorage();
      console.log('Loading book from storage:', actualBookId);
      book = await storage.getBook(actualBookId);

      if (!book) {
        error = 'Book not found';
        loading = false;
        console.error('Book not found in storage:', actualBookId);
        currentBook.set(null);
        return;
      }
      console.log('Book loaded:', book.title);

      // Update current book in store for header display
      currentBook.set(book);

      // Create blob URLs for bundle assets
      if (book.bundle && book.bundle.assets) {
        for (const [path, asset] of Object.entries(book.bundle.assets)) {
          // Convert asset data to Blob
          let blob: Blob;
          if (typeof asset.data === 'string') {
            // Base64 string
            const binaryString = atob(asset.data);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            blob = new Blob([bytes], { type: asset.mimeType });
          } else {
            // ArrayBuffer
            blob = new Blob([asset.data], { type: asset.mimeType });
          }

          const url = URL.createObjectURL(blob);
          assetUrls.set(asset.path, url);
        }
      }

      // Render markdown content
      let content = renderer.render(book.content, {
        baseUrl: book.bundle?.baseUrl
      });

      // Replace asset paths with blob URLs
      for (const [path, url] of assetUrls) {
        // Replace both with and without leading slash
        content = content.replace(new RegExp(path.replace(/\//g, '\\/'), 'g'), url);
        content = content.replace(new RegExp(path.replace(/^\//, '').replace(/\//g, '\\/'), 'g'), url);
      }

      renderedContent = content;

      // Extract TOC
      toc = renderer.extractTOC(book.content);

      // Update last read time
      await storage.updateBook(actualBookId, { lastReadAt: new Date() });

      loading = false;
      console.log('=== READER DEBUG END (SUCCESS) ===');

      // Restore reading position after content is loaded
      setTimeout(async () => {
        if (positionTracker) {
          const scrollPercentage = await positionTracker.restorePosition();
          if (scrollPercentage !== null && contentContainer) {
            const scrollHeight = contentContainer.scrollHeight - contentContainer.clientHeight;
            const scrollTop = (scrollPercentage / 100) * scrollHeight;
            contentContainer.scrollTop = scrollTop;
          }

          // Start tracking scroll position
          if (contentContainer) {
            positionTracker.startTracking(contentContainer);
          }
        }
      }, 100);
    } catch (err) {
      error = err instanceof Error ? err.message : 'Failed to load book';
      loading = false;
      console.error('=== READER DEBUG END (ERROR) ===', err);
    }
  });

  onDestroy(() => {
    if (contentContainer && positionTracker) {
      positionTracker.stopTracking(contentContainer);
    }

    // Revoke all blob URLs to free memory
    for (const url of assetUrls.values()) {
      URL.revokeObjectURL(url);
    }
    assetUrls.clear();

    // Clear current book from store
    currentBook.set(null);
  });

  function scrollToHeading(id: string) {
    if (!contentContainer) return;

    const element = document.getElementById(id);
    if (element) {
      // Calculate position relative to the scrollable container
      const containerRect = contentContainer.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      const scrollTop = elementRect.top - containerRect.top + contentContainer.scrollTop;

      // Smooth scroll to the element
      contentContainer.scrollTo({
        top: scrollTop - 20, // 20px offset from top
        behavior: 'smooth'
      });

      // Close sidebar on mobile after clicking TOC item
      if (window.innerWidth < 768) {
        sidebarOpen = false;
      }
    }
  }
</script>

{#if loading}
  <div class="flex items-center justify-center" style="height: calc(100vh - 4rem);">
    <p class="text-gray-500">Loading book...</p>
  </div>
{:else if error}
  <div class="flex items-center justify-center" style="height: calc(100vh - 4rem);">
    <div class="p-4 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-200 rounded">
      {error}
    </div>
  </div>
{:else if book}
  <!-- Reader container:
       Mobile: full screen
       Desktop: height 100% (parent sets boundaries with fixed inset-0)
  -->
  <div class="reader-container flex h-full">
    <!-- Sidebar Overlay (mobile only) -->
    {#if sidebarOpen}
      <div
        class="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
        on:click={toggleSidebar}
      ></div>
    {/if}

    <!-- TOC Sidebar -->
    {#if sidebarOpen}
    <aside
      class="toc-sidebar border-r dark:border-gray-700 overflow-y-auto bg-white dark:bg-gray-900 transition-all duration-300 ease-in-out
        fixed md:relative left-0 md:left-0 inset-y-0 md:inset-y-auto z-50 md:z-auto md:h-full"
      style="width: {sidebarWidth}px;"
    >
      <!-- Resize Handle (desktop only) -->
      <div
        class="hidden md:block absolute right-0 top-0 bottom-0 w-1 hover:w-2 bg-gray-300 dark:bg-gray-600 hover:bg-blue-500 cursor-col-resize transition-all"
        on:mousedown={startResize}
        role="separator"
        aria-label="Resize sidebar"
      ></div>
      <div class="p-4">
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-lg font-semibold">{book.title}</h2>
          <button
            on:click={toggleSidebar}
            class="md:hidden p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
            aria-label="Close sidebar"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <nav class="toc-nav">
          {#each toc as entry}
            <div class="toc-entry level-{entry.level}">
              <button
                on:click={() => scrollToHeading(entry.id)}
                class="text-left w-full py-1 px-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded text-sm"
                style="padding-left: {(entry.level - 1) * 1}rem"
              >
                {entry.text}
              </button>
              {#if entry.children && entry.children.length > 0}
                {#each entry.children as child}
                  <div class="toc-entry level-{child.level}">
                    <button
                      on:click={() => scrollToHeading(child.id)}
                      class="text-left w-full py-1 px-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded text-sm"
                      style="padding-left: {(child.level - 1) * 1}rem"
                    >
                      {child.text}
                    </button>
                    {#if child.children && child.children.length > 0}
                      {#each child.children as grandchild}
                        <div class="toc-entry level-{grandchild.level}">
                          <button
                            on:click={() => scrollToHeading(grandchild.id)}
                            class="text-left w-full py-1 px-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded text-sm"
                            style="padding-left: {(grandchild.level - 1) * 1}rem"
                          >
                            {grandchild.text}
                          </button>
                        </div>
                      {/each}
                    {/if}
                  </div>
                {/each}
              {/if}
            </div>
          {/each}
        </nav>
      </div>
    </aside>
    {/if}

    <!-- Main reading area -->
    <div class="reader-content flex-1 overflow-y-auto bg-white dark:bg-gray-900" bind:this={contentContainer}>
      <div class="max-w-4xl mx-auto px-8 py-8">
        <div class="prose prose-lg dark:prose-invert reader-typography">
          {@html renderedContent}
        </div>
      </div>
    </div>
  </div>
{/if}
